/**
 * SignalsmithWorkerEngine — main-thread façade for the Signalsmith Stretch worker.
 *
 * Drop-in replacement for the (removed, GPL-v2) RubberbandEngine: same surface
 * (shift / preload / terminate / isAvailable / loadedFrom) so AudioPipeline and
 * the few tools that called the engine directly (recording-studio,
 * shadowing-studio) keep working with only a name swap.
 *
 * All the DSP runs in a dedicated Web Worker (`signalsmith-worker.js`). The main
 * thread only:
 *   1. copies channels out of the AudioBuffer into Float32Array and transfers
 *      them — zero-copy hand-off.
 *   2. listens for progress messages and re-calls onProgress.
 *   3. wraps the returned channel arrays back into a fresh AudioBuffer.
 * That keeps the UI at 60 fps on long files — the reason we run Signalsmith in a
 * plain Worker instead of the AudioWorklet-in-OfflineAudioContext path that the
 * legacy PitchEngine uses (which renders on the main thread and freezes for
 * several seconds on multi-minute audio).
 *
 * Engine = Signalsmith Stretch 1.3.2 (MIT). Identical library version to the
 * AudioWorklet PitchEngine, so output quality is the same; PitchEngine remains
 * the automatic fallback if this worker's module fails to load.
 */
(function () {
'use strict';

/* Versioned at RUNTIME. nginx serves /js/*.js as `immutable, max-age=15552000`
   on the intl edge, and `new Worker('/js/…')` never passes through @light — so a
   returning visitor keeps the worker they first downloaded for 180 DAYS while
   the deploy reports success. MEASURED 2026-09-01, the day the pitch-alignment
   fix shipped: timbrica.com served the new worker, tembrica.com still served the
   22 July build (different ETag, different size) — i.e. the fix reached RU and
   not intl. A `?v=` query is a different edge cache key (verified: the versioned
   URL answers cf-cache-status: MISS), so this both delivers today's fix and stops
   the next one from sticking. `typeof window` guarded: this file is also reachable
   from worker contexts, where `window` does not exist. */
var WARP_MAP_URL = (typeof window !== 'undefined' && typeof window.__jsSrc === 'function')
    ? window.__jsSrc('/js/warp-map.js')
    : '/js/warp-map.js';

var WORKER_URL = (typeof window !== 'undefined' && typeof window.__jsSrc === 'function')
    ? window.__jsSrc('/js/signalsmith-worker.js')
    : '/js/signalsmith-worker.js';
/* The output ceiling the worker applies (signalsmith-worker.js applyCeiling):
   its two modules travel VERSIONED for the same reason as WARP_MAP_URL — an
   edit to them does not change the worker's own mtime. */
var CEILING_URLS = (typeof window !== 'undefined' && typeof window.__jsSrc === 'function')
    ? [window.__jsSrc('/js/mastering-limiter.js'), window.__jsSrc('/js/export-ceiling.js')]
    : ['/js/mastering-limiter.js', '/js/export-ceiling.js'];
var _workerId = 0;
var _loadedSource = null;

/* Lanes (2026-09-24): one worker runs one job at a time, so a caller with several
   INDEPENDENT jobs (a mashup's vocal + the music's drums, bass and other stems) waited
   for them one after another — a key change on a 3.5-min pair re-rendered three stems
   in a row. `opts.lane` (0…MAX_LANES-1) puts a job on its own worker; lane 0 is the
   worker every caller used before, so a caller that never passes `lane` gets exactly
   the old behaviour. Each lane has its own pending map and its own watchdog: a hung
   job kills only its own worker. */
var MAX_LANES = 4;
var _lanes = [];
function lane(i) {
    i = Math.max(0, Math.min(MAX_LANES - 1, (i | 0)));
    if (!_lanes[i]) _lanes[i] = { i: i, worker: null, pending: new Map(), wd: null };
    return _lanes[i];
}

/* `opts.release` (2026-10-01): when the job is over and nothing else waits on its lane, the
   lane's worker goes. An idle worker keeps what the job left behind — its wasm memory never
   shrinks, and the full-length arrays of the render and of the output ceiling sit in its heap
   as garbage nobody collects while it has nothing to do: 125 MB after /mashup-maker's two vocal
   renders on the 4-GB-phone profile of the bug report of 2026-09-28 (terminate() gave all of it
   back). The next job starts a fresh worker — the module is ~100 KB, one compile. For a caller
   that renders once and then waits for the person, on a device short of memory; a slider that
   re-renders every second keeps its worker by not passing it. */
function releaseIdle(L) {
    if (!L.worker || L.pending.size) return;
    var w = L.worker;
    L.worker = null;
    if (L.wd) L.wd.dispose();
    try { w.terminate(); } catch (_) {}
}

// Heartbeat watchdog — same protection the rubberband + mp3 workers used.
// Signalsmith's process loop posts progress every ~2% of output; 30 s of
// silence means the OS killed the worker (WASM heap spike on a low-RAM device).
function watchdog(L) {
    if (L.wd) return L.wd;
    if (!window.WorkerWatchdog) return null;
    L.wd = window.WorkerWatchdog.create({
        name: 'signalsmith' + (L.i ? '-' + L.i : ''),
        getPending: function () { return L.pending; },
        terminateWorker: function () {
            try { if (L.worker) L.worker.terminate(); } catch (_) {}
            L.worker = null;
        },
        getHeartbeatTimeoutMs: function () {
            return Number(window.__SIGNALSMITH_WORKER_TIMEOUT_OVERRIDE) || 30000;
        },
        getTickMs: function () {
            return Number(window.__SIGNALSMITH_WATCHDOG_TICK_OVERRIDE) || 5000;
        },
    });
    return L.wd;
}

function getWorker(L) {
    if (L.worker) return L.worker;
    var w = L.worker = new Worker(WORKER_URL);
    w.onmessage = function (ev) {
        var msg = ev.data;
        var p = L.pending.get(msg.id);
        if (!p) return;
        var wd = watchdog(L); if (wd) wd.touch(msg.id);
        if (msg.type === 'progress' && p.onProgress) { p.onProgress(msg.pct); return; }
        if (msg.type === 'done') {
            _loadedSource = msg.source || _loadedSource;
            L.pending.delete(msg.id);
            if (wd) wd.forget(msg.id);
            p.resolve({ channels: msg.channels, source: msg.source, ceiling: msg.ceiling || null });
            if (p.release && L.worker === w) releaseIdle(L);
            return;
        }
        if (msg.type === 'error') {
            L.pending.delete(msg.id);
            if (wd) wd.forget(msg.id);
            p.reject(new Error(msg.error || 'signalsmith worker error'));
            if (p.release && L.worker === w) releaseIdle(L);
            return;
        }
    };
    w.onerror = function (err) {
        // Fatal worker crash — reject everything pending on THIS lane and recycle on next call.
        L.pending.forEach(function (p) { p.reject(new Error('signalsmith worker crashed: ' + (err.message || err))); });
        L.pending.clear();
        var wd = watchdog(L); if (wd) wd.dispose();
        try { w.terminate(); } catch (_) {}
        if (L.worker === w) L.worker = null;
    };
    return w;
}

function emptyBuffer(ch, length, sr) {
    if (typeof window.__safeAudioBuffer === 'function') {
        return window.__safeAudioBuffer({ numberOfChannels: ch, length: length, sampleRate: sr });
    }
    if (typeof AudioBuffer === 'function') {
        try { return new AudioBuffer({ numberOfChannels: ch, length: length, sampleRate: sr }); }
        catch (_) {}
    }
    var ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(ch, length, sr);
    return ctx.createBuffer(ch, length, sr);
}

async function shift(audioBuffer, opts) {
    opts = opts || {};
    var semitones = Number(opts.semitones) || 0;
    var preserveFormants = !!opts.preserveFormants;
    var formantSemitones = Number(opts.formantSemitones) || 0;
    var rate = Number(opts.rate) || 1;
    if (rate <= 0) rate = 1;
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;

    // Кривая натяжения: пары «время входа → время выхода» (см. warp-map.js).
    // План считается ЗДЕСЬ, чтобы отказ пришёл до запуска воркера и вызывающий
    // успел спуститься на ступень ниже (постоянный коэффициент → смещение →
    // руками), а не получил молча искажённый звук.
    // ⚠️ Кривая проверяется, КАК ТОЛЬКО её передали, а не когда она выглядит
    // годной: негодную нельзя молча выбросить и сделать вид, что натянули —
    // вызывающий получил бы нетронутый звук и «успех».
    var warpPlan = null;
    if (opts.warp != null) {
        if (!window.WarpMap) throw new Error('warp_module_missing');
        warpPlan = window.WarpMap.build(opts.warp, {
            sampleRate: audioBuffer.sampleRate,
            inFrames: audioBuffer.length
        });
        if (warpPlan.error) throw new Error('warp_' + warpPlan.error);
    }

    var isNoOp = !warpPlan
        && Math.abs(semitones) < 0.001
        && Math.abs(rate - 1) < 0.001
        && !preserveFormants
        && formantSemitones === 0;
    if (isNoOp) {
        var copy = emptyBuffer(audioBuffer.numberOfChannels, audioBuffer.length, audioBuffer.sampleRate);
        for (var c = 0; c < audioBuffer.numberOfChannels; c++) {
            copy.getChannelData(c).set(audioBuffer.getChannelData(c));
        }
        return copy;
    }

    var ch = audioBuffer.numberOfChannels;
    var sr = audioBuffer.sampleRate;

    // Copy channels into fresh Float32Arrays we can transfer to the worker.
    // new Float32Array(getChannelData) owns its own ArrayBuffer, so transferring
    // it doesn't detach the AudioBuffer's internal storage — subsequent reads
    // (waveform redraw, re-export with different settings) still work.
    var channels = [];
    var transfers = [];
    for (var c2 = 0; c2 < ch; c2++) {
        // `opts.transferInput` (2026-10-01): the caller made these arrays for this one call (a plain
        // {getChannelData} object, never an AudioBuffer) — they go to the worker as they are and are
        // detached here. A copy of a stem opened only to be fitted was one more stem of garbage per
        // job (/mashup-maker on a 4-GB phone); a caller that retries makes its input again.
        var own = audioBuffer.getChannelData(c2);
        var copyArr = (opts.transferInput && own.byteOffset === 0 && own.byteLength === own.buffer.byteLength && transfers.indexOf(own.buffer) < 0)
            ? own : new Float32Array(own);
        channels.push(copyArr);
        transfers.push(copyArr.buffer);
    }

    var result = await new Promise(function (resolve, reject) {
        var id = ++_workerId;
        var L = lane(opts.lane);
        L.pending.set(id, { resolve: resolve, reject: reject, onProgress: onProgress, release: !!opts.release });
        var wd = watchdog(L); if (wd) wd.mark(id);
        var worker = getWorker(L);
        var msg = {
            id: id,
            type: 'process',
            channels: channels,
            sampleRate: sr,
            semitones: semitones,
            rate: rate,
            formantPreserved: preserveFormants,
            formantSemitones: formantSemitones,
            ceilingUrls: CEILING_URLS
        };
        if (window.__stretchCeilingOff) msg.ceilingOff = true;   // guard stands only
        if (warpPlan) {
            msg.warpPts = warpPlan.pts;
            msg.warpOutFrames = warpPlan.outFrames;
            msg.warpMaxRate = warpPlan.maxRate;
            msg.warpRate0 = warpPlan.rate0;
            // ⚠️ Версия зависимости обязана быть в URL, который получает
            // воркер: правка warp-map.js не меняет mtime самого воркера, а
            // статика отдаётся на год (см. шапку про __jsSrc).
            msg.warpMapUrl = WARP_MAP_URL;
            transfers.push(warpPlan.pts.buffer);
        }
        worker.postMessage(msg, transfers);
    });

    var outChannels = result.channels;
    var outLen = (outChannels[0] && outChannels[0].length) || 0;
    var out = emptyBuffer(ch, Math.max(1, outLen), sr);
    for (var c3 = 0; c3 < ch; c3++) {
        if (outChannels[c3] && outChannels[c3].length) out.getChannelData(c3).set(outChannels[c3]);
    }
    // How often the engine pushes a file past full scale, and by how much —
    // only when the ceiling actually engaged (or could not load).
    if (result.ceiling) {
        try {
            window.__ae && window.__ae('feature_use', {
                slug: opts.slug || window.__TOOL_SLUG || 'unknown', f: 'stretch_ceiling',
                over_db: result.ceiling.overDb, gr_db: result.ceiling.grDb, err: result.ceiling.error || undefined,
                rate: Math.round(rate * 1000) / 1000, semi: Math.round(semitones * 10) / 10
            });
        } catch (_) {}
    }
    return out;
}

function preload() {
    // Spawn the worker so the WASM compile starts in the background — by the
    // time the user moves a slider, the engine is already warm.
    try { getWorker(lane(0)).postMessage({ id: 0, type: 'noop' }); } catch (_) {}
    return Promise.resolve();
}

function terminate() {
    _lanes.forEach(function (L) {
        if (!L) return;
        if (L.worker) { try { L.worker.terminate(); } catch (_) {} L.worker = null; }
        L.pending.forEach(function (p) { p.reject(new Error('terminated')); });
        L.pending.clear();
    });
}

window.SignalsmithWorkerEngine = {
    shift: shift,
    preload: preload,
    terminate: terminate,
    loadedFrom: function () { return _loadedSource; },
    isAvailable: function () { return _lanes.some(function (L) { return L && !!L.worker; }) || !!_loadedSource; },
    maxLanes: MAX_LANES,
    engine: 'signalsmith-stretch@1.3.2 (worker)',
    version: '1.0.0'
};

})();
